import { useState } from 'react';
import { useSessionStore } from '@/state/session-store';
import type { QuestionType, RehearsalDifficulty } from '@/types';

interface Props {
  onToggleMode: (isRehearsal: boolean) => void;
  isRehearsalMode: boolean;
}

const DIFFICULTY_OPTIONS: { value: RehearsalDifficulty; label: string; description: string; disabled: boolean }[] = [
  {
    value: 'easy',
    label: 'Easy',
    description: 'Questions after each talking point. Answer at your own pace.',
    disabled: false,
  },
  {
    value: 'medium',
    label: 'Medium',
    description: '15-second timer to answer. More challenging questions.',
    disabled: false, // Enabled in Phase 2
  },
  {
    value: 'hard',
    label: 'Hard',
    description: 'Voice interrupts require verbal response.',
    disabled: true, // Phase 3
  },
];

// Base question types (available in Easy mode)
const BASE_QUESTION_TYPES: { value: QuestionType; label: string; description: string }[] = [
  {
    value: 'talking_point',
    label: 'Talking Point Recall',
    description: 'Test recall of key messages',
  },
  {
    value: 'technical',
    label: 'Technical Accuracy',
    description: 'Challenge claims with deeper questions',
  },
];

// Advanced question types (available in Medium+ difficulty)
const ADVANCED_QUESTION_TYPES: { value: QuestionType; label: string; description: string }[] = [
  {
    value: 'contrarian',
    label: 'Objection Handling',
    description: 'Respond to skeptical pushback',
  },
  {
    value: 'clarification',
    label: 'Clarification',
    description: 'Explain concepts differently',
  },
  {
    value: 'edge_case',
    label: 'Edge Cases',
    description: 'Handle unusual scenarios',
  },
];

export function RehearsalSetup({ onToggleMode, isRehearsalMode }: Props) {
  const { enableRehearsalMode, disableRehearsalMode } = useSessionStore();

  const [difficulty, setDifficulty] = useState<RehearsalDifficulty>('easy');
  const [questionFocus, setQuestionFocus] = useState<QuestionType[]>(['talking_point', 'technical']);
  const [noQuestions, setNoQuestions] = useState(false);

  const handleModeToggle = (rehearsal: boolean) => {
    if (rehearsal) {
      enableRehearsalMode({ difficulty, questionFocus, noQuestions });
    } else {
      disableRehearsalMode();
    }
    onToggleMode(rehearsal);
  };

  const handleDifficultyChange = (value: RehearsalDifficulty) => {
    setDifficulty(value);
    if (isRehearsalMode) {
      enableRehearsalMode({ difficulty: value, questionFocus, noQuestions });
    }
  };

  const handleQuestionTypeToggle = (type: QuestionType) => {
    const newFocus = questionFocus.includes(type)
      ? questionFocus.filter(t => t !== type)
      : [...questionFocus, type];

    // Ensure at least one type is selected
    if (newFocus.length === 0) return;

    setQuestionFocus(newFocus);
    if (isRehearsalMode) {
      enableRehearsalMode({ difficulty, questionFocus: newFocus, noQuestions });
    }
  };

  const handleNoQuestionsToggle = (enabled: boolean) => {
    setNoQuestions(enabled);
    if (isRehearsalMode) {
      enableRehearsalMode({ difficulty, questionFocus, noQuestions: enabled });
    }
  };

  return (
    <div className="space-y-4">
      {/* Session Type Toggle */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Session Type
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleModeToggle(false)}
            className={`py-3 px-4 text-sm rounded-lg border-2 transition-all ${
              !isRehearsalMode
                ? 'bg-green-50 text-green-700 border-green-500'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="font-medium">Live Call</div>
            <div className="text-xs text-gray-500 mt-0.5">Track coverage</div>
          </button>
          <button
            onClick={() => handleModeToggle(true)}
            className={`py-3 px-4 text-sm rounded-lg border-2 transition-all ${
              isRehearsalMode
                ? 'bg-blue-50 text-blue-700 border-blue-500'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="font-medium">Rehearsal Mode</div>
            <div className="text-xs text-gray-500 mt-0.5">Practice with Q&A</div>
          </button>
        </div>
      </div>

      {/* Rehearsal Settings (only shown when rehearsal mode is active) */}
      {isRehearsalMode && (
        <>
          {/* Difficulty Selection */}
          <div className="space-y-2 pt-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Difficulty
            </label>
            <div className="space-y-2">
              {DIFFICULTY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => !opt.disabled && handleDifficultyChange(opt.value)}
                  disabled={opt.disabled}
                  className={`w-full py-2.5 px-3 text-left rounded-lg border transition-all ${
                    opt.disabled
                      ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                      : difficulty === opt.value
                        ? 'bg-blue-50 text-blue-700 border-blue-500'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{opt.label}</span>
                    {opt.disabled && (
                      <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded">Coming Soon</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* No Questions Toggle */}
          <div className="space-y-2 pt-2">
            <label
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors"
            >
              <div>
                <span className="text-sm font-medium text-gray-700">No Questions</span>
                <p className="text-xs text-gray-500">Practice without Q&A interruptions</p>
              </div>
              <button
                type="button"
                onClick={() => handleNoQuestionsToggle(!noQuestions)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  noQuestions ? 'bg-blue-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    noQuestions ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Question Types - only show when questions are enabled */}
          {!noQuestions && (
          <div className="space-y-2 pt-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Question Focus
            </label>
            <div className="space-y-2">
              {/* Base question types (always available) */}
              {BASE_QUESTION_TYPES.map(opt => (
                <label
                  key={opt.value}
                  className="flex items-start gap-3 p-2.5 rounded-lg border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={questionFocus.includes(opt.value)}
                    onChange={() => handleQuestionTypeToggle(opt.value)}
                    className="mt-0.5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{opt.label}</span>
                    <p className="text-xs text-gray-500">{opt.description}</p>
                  </div>
                </label>
              ))}

              {/* Advanced question types (Medium+ only) */}
              {difficulty !== 'easy' && (
                <>
                  <div className="text-xs text-gray-400 uppercase tracking-wide mt-3 mb-1">
                    Advanced (Medium+)
                  </div>
                  {ADVANCED_QUESTION_TYPES.map(opt => (
                    <label
                      key={opt.value}
                      className="flex items-start gap-3 p-2.5 rounded-lg border border-orange-200 cursor-pointer hover:border-orange-300 transition-colors bg-orange-50/50"
                    >
                      <input
                        type="checkbox"
                        checked={questionFocus.includes(opt.value)}
                        onChange={() => handleQuestionTypeToggle(opt.value)}
                        className="mt-0.5 rounded border-orange-300 text-orange-500 focus:ring-orange-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700">{opt.label}</span>
                        <p className="text-xs text-gray-500">{opt.description}</p>
                      </div>
                    </label>
                  ))}
                </>
              )}
            </div>
          </div>
          )}

          {/* Info Box - Updated for mode */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              {noQuestions ? (
                <>
                  <strong>Practice Mode:</strong> Just practice your pitch with coverage tracking.
                  No questions will be asked.
                </>
              ) : difficulty === 'easy' ? (
                <>
                  <strong>Easy Mode:</strong> Practice your pitch and receive questions after each talking point.
                  Answer at your own pace.
                </>
              ) : difficulty === 'medium' ? (
                <>
                  <strong>Medium Mode:</strong> You have 15 seconds to answer each question.
                  Say "To answer your question..." to respond verbally, or click to type.
                </>
              ) : (
                <>
                  <strong>Hard Mode:</strong> Questions will interrupt your pitch.
                  Respond verbally within the time limit.
                </>
              )}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
